import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Plus, Trash2, AlertCircle, RotateCcw, FlaskConical, Lightbulb } from "lucide-react";
import RouteIcon from "@/components/RouteIcon";

const blankDrug = {
  drug_name: "", brand: "", dose: "", route: "Intravenous",
  location: "Facility Administered", days_pattern: "1",
  infusion_duration_min: "", observation_time_min: "",
  dose_cap: "", warnings: "",
};
const blankCycle = () => ({ label: "Cycle 1", length_days: 21, drugs: [{ ...blankDrug }] });

const normaliseLoadedCycles = (cycles = []) =>
  cycles.map((c) => ({
    ...c,
    drugs: (c.drugs || []).map((d) => ({
      ...blankDrug, ...d,
      brand: d.brand || "",
      infusion_duration_min: d.infusion_duration_min ?? "",
      observation_time_min: d.observation_time_min ?? "",
      dose_cap: d.dose_cap || "",
      warnings: d.warnings || "",
    })),
  }));

export default function RegimenCreatePage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState({ name: "", disease_type: "", description: "", visibility: "owned", cycles: [blankCycle()] });
  const [err, setErr] = useState("");
  const [dupModal, setDupModal] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [meta, setMeta] = useState({ approval_status: null, has_snapshot: false });
  const [library, setLibrary] = useState([]);
  const [startFromId, setStartFromId] = useState("");
  const nav = useNavigate();

  // Load the regimen library for the "Start from existing" picker (only in
  // create mode — when editing we already have the full doc loaded below).
  useEffect(() => {
    if (isEdit) return;
    api.get("/regimens").then((r) => setLibrary(r.data || [])).catch(() => {});
  }, [isEdit]);

  const startFromExisting = (regId) => {
    setStartFromId(regId);
    if (!regId) {
      setForm({ name: "", disease_type: "", description: "", visibility: "owned", cycles: [blankCycle()] });
      return;
    }
    const src = library.find((r) => r.id === regId);
    if (!src) return;
    setForm({
      name: `${src.name} (modified)`,
      disease_type: src.disease_type || "",
      description: src.description || "",
      visibility: "owned",
      cycles: normaliseLoadedCycles(src.cycles),
    });
  };

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/regimens/${id}`).then((res) => {
      const r = res.data;
      setForm({
        name: r.name || "",
        disease_type: r.disease_type || "",
        description: r.description || "",
        visibility: r.visibility || "owned",
        cycles: normaliseLoadedCycles(r.cycles),
      });
      setMeta({
        approval_status: r.approval_status || "approved",
        has_snapshot: Boolean(r.last_approved_snapshot),
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id, isEdit]);

  const restoreLastApproved = async () => {
    if (!window.confirm("Restore this regimen to its last approved version? Any pending edits will be discarded.")) return;
    try {
      const r = await api.post(`/regimens/${id}/restore`);
      // After restore, hop back to the detail page so the user sees the now-approved doc.
      nav(`/regimens/${r.data.id}`);
    } catch (e) {
      alert(e.response?.data?.detail || "Restore failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    const payload = {
      ...form,
      cycles: form.cycles.map((c) => ({
        ...c,
        drugs: c.drugs.map((d) => ({
          ...d,
          infusion_duration_min: d.infusion_duration_min === "" ? null : Number(d.infusion_duration_min),
          observation_time_min: d.observation_time_min === "" ? null : Number(d.observation_time_min),
          dose_cap: d.dose_cap || null,
          warnings: d.warnings || null,
          brand: d.brand || null,
        })),
      })),
    };
    try {
      const r = isEdit ? await api.put(`/regimens/${id}`, payload) : await api.post("/regimens", payload);
      nav(`/regimens/${r.data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail || "Save failed";
      if (e.response?.status === 409 || /already exists/i.test(detail)) {
        setDupModal(true);
      } else {
        setErr(detail);
      }
    }
  };

  const setCycle = (i, patch) => setForm({ ...form, cycles: form.cycles.map((c,j)=> j===i?{...c,...patch}:c) });
  const setDrug = (ci, di, patch) => setCycle(ci, { drugs: form.cycles[ci].drugs.map((d,j)=> j===di?{...d,...patch}:d) });
  const addCycle = () => setForm({ ...form, cycles: [...form.cycles, { label: `Cycle ${form.cycles.length+1}`, length_days: 21, drugs: [{ ...blankDrug }] }] });
  const addDrug = (ci) => setCycle(ci, { drugs: [...form.cycles[ci].drugs, { ...blankDrug }] });
  const rmCycle = (ci) => form.cycles.length > 1 && setForm({ ...form, cycles: form.cycles.filter((_,i)=>i!==ci) });
  const rmDrug = (ci, di) => form.cycles[ci].drugs.length > 1 && setCycle(ci, { drugs: form.cycles[ci].drugs.filter((_,i)=>i!==di) });

  return (
    <div className="p-8 max-w-[1100px]" data-testid="regimen-create-page">
      <Link to="/regimens" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-900 text-sm mb-4">
        <ArrowLeft className="w-4 h-4" />Back
      </Link>
      <h1 className="font-display text-3xl font-bold text-stone-900 mb-6">{isEdit ? "Edit regimen" : "New regimen"}</h1>
      {!isEdit && library.length > 0 && (
        <div className="mb-5 px-4 py-3 rounded-xl border bg-white border-stone-200 shadow-soft" data-testid="start-from-existing-card">
          <label className="text-sm font-medium text-stone-700 flex items-center gap-2 mb-1.5">
            <FlaskConical className="w-4 h-4 text-brand-500" />
            Start from an existing regimen <span className="text-stone-400 font-normal">(optional)</span>
          </label>
          <select
            data-testid="start-from-existing-select"
            value={startFromId}
            onChange={(e) => startFromExisting(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none text-sm"
          >
            <option value="">— Blank regimen —</option>
            {library.map((r) => (
              <option key={r.id} value={r.id}>{r.name} · {r.disease_type} · {r.cycles?.length || 0} cycle{(r.cycles?.length || 0) !== 1 ? "s" : ""}</option>
            ))}
          </select>
          <p className="text-xs text-stone-500 mt-1.5">Picking a regimen pre-fills the cycles below — modify any field to make a unique regimen.</p>
        </div>
      )}
      {isEdit && meta.has_snapshot && (
        <div className="mb-5 flex items-start gap-3 px-4 py-3 rounded-lg border bg-amber-50 border-amber-200" data-testid="restore-banner">
          <RotateCcw className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
          <div className="flex-1 text-sm text-amber-900">
            <b>This regimen has unapproved edits.</b> {meta.approval_status === "pending" ? "It is currently awaiting admin approval." : null} You can revert to the last approved version with one click — no admin re-approval needed.
          </div>
          <button
            type="button"
            onClick={restoreLastApproved}
            data-testid="restore-last-approved-btn"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 text-sm font-semibold"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Restore last approved
          </button>
        </div>
      )}
      {err && <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}
      <form onSubmit={submit} className="space-y-6">
        <div className="bg-white border border-stone-200 rounded-xl shadow-soft p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">Regimen name*</label>
              <input data-testid="regimen-name-input" required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}
                className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">Disease type*</label>
              <input data-testid="regimen-disease-input" required value={form.disease_type} onChange={(e)=>setForm({...form,disease_type:e.target.value})}
                className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">Description</label>
            <textarea data-testid="regimen-description-input" value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} rows={2}
              className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
          </div>
        </div>

        {form.cycles.map((c, ci) => (
          <div key={ci} className="bg-white border border-stone-200 rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <input value={c.label} onChange={(e)=>setCycle(ci,{label:e.target.value})}
                  className="font-display font-semibold text-stone-900 px-2 py-1 rounded-md border border-transparent hover:border-stone-200 focus:border-brand-500 focus:outline-none" />
                <span className="text-sm text-stone-500">Length</span>
                <input type="number" min={1} value={c.length_days} onChange={(e)=>setCycle(ci,{length_days:parseInt(e.target.value)||0})}
                  className="w-20 px-2 py-1 rounded-md border border-stone-300 text-sm focus:border-brand-500 focus:outline-none" />
                <span className="text-sm text-stone-500">days</span>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => addDrug(ci)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-stone-50 hover:bg-stone-100 text-xs font-medium border border-stone-200" data-testid={`add-drug-${ci}`}><Plus className="w-3.5 h-3.5" />Drug</button>
                {form.cycles.length > 1 && <button type="button" onClick={() => rmCycle(ci)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-xs font-medium border border-red-200"><Trash2 className="w-3.5 h-3.5" />Remove cycle</button>}
              </div>
            </div>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="text-left p-2">Drug *</th>
                    <th className="text-left p-2">Brand</th>
                    <th className="text-left p-2">Dose *</th>
                    <th className="text-left p-2">Days *</th>
                    <th className="text-left p-2">Route *</th>
                    <th className="text-left p-2">Location *</th>
                    <th className="text-left p-2 whitespace-nowrap" title="Infusion duration (min)">Infusion (m)</th>
                    <th className="text-left p-2 whitespace-nowrap" title="Observation time (min)">Obs. (m)</th>
                    <th className="text-left p-2">Dose cap</th>
                    <th className="text-left p-2">Warnings</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {c.drugs.map((d, di) => (
                    <tr key={di} className="border-t border-stone-100 align-top">
                      <td className="p-1">
                        <input value={d.drug_name} onChange={(e)=>setDrug(ci,di,{drug_name:e.target.value})} required
                          className="w-full px-2 py-1.5 rounded-md border border-stone-200 focus:border-brand-500 focus:outline-none text-sm" />
                      </td>
                      <td className="p-1">
                        <input value={d.brand} onChange={(e)=>setDrug(ci,di,{brand:e.target.value})}
                          className="w-full px-2 py-1.5 rounded-md border border-stone-200 focus:border-brand-500 focus:outline-none text-sm" />
                      </td>
                      <td className="p-1">
                        <input value={d.dose} onChange={(e)=>setDrug(ci,di,{dose:e.target.value})} required
                          className="w-full px-2 py-1.5 rounded-md border border-stone-200 focus:border-brand-500 focus:outline-none text-sm" />
                      </td>
                      <td className="p-1">
                        <input value={d.days_pattern} onChange={(e)=>setDrug(ci,di,{days_pattern:e.target.value})} placeholder="1,4,8,11 or 1-21"
                          className="w-full px-2 py-1.5 rounded-md border border-stone-200 focus:border-brand-500 focus:outline-none text-sm font-mono" />
                      </td>
                      <td className="p-1">
                        <div className="flex items-center gap-1.5">
                          <RouteIcon route={d.route} size="sm" />
                          <select value={d.route} onChange={(e)=>setDrug(ci,di,{route:e.target.value})} className="flex-1 px-2 py-1.5 rounded-md border border-stone-200 focus:border-brand-500 text-sm">
                            {["Intravenous","Oral","Subcutaneous","Intramuscular","Topical"].map(o=><option key={o}>{o}</option>)}
                          </select>
                        </div>
                      </td>
                      <td className="p-1">
                        <select value={d.location} onChange={(e)=>setDrug(ci,di,{location:e.target.value})} className="w-full px-2 py-1.5 rounded-md border border-stone-200 focus:border-brand-500 text-sm">
                          {["Facility Administered","Home Administration","Inpatient Admission","Outpatient"].map(o=><option key={o}>{o}</option>)}
                        </select>
                      </td>
                      <td className="p-1">
                        <input type="number" min={0} value={d.infusion_duration_min} onChange={(e)=>setDrug(ci,di,{infusion_duration_min:e.target.value})}
                          placeholder="opt"
                          className="w-20 px-2 py-1.5 rounded-md border border-stone-200 focus:border-brand-500 focus:outline-none text-sm" />
                      </td>
                      <td className="p-1">
                        <input type="number" min={0} value={d.observation_time_min} onChange={(e)=>setDrug(ci,di,{observation_time_min:e.target.value})}
                          placeholder="opt"
                          className="w-20 px-2 py-1.5 rounded-md border border-stone-200 focus:border-brand-500 focus:outline-none text-sm" />
                      </td>
                      <td className="p-1">
                        <input value={d.dose_cap} onChange={(e)=>setDrug(ci,di,{dose_cap:e.target.value})} placeholder="opt"
                          className="w-24 px-2 py-1.5 rounded-md border border-stone-200 focus:border-brand-500 focus:outline-none text-sm" />
                      </td>
                      <td className="p-1">
                        <input value={d.warnings} onChange={(e)=>setDrug(ci,di,{warnings:e.target.value})} placeholder="e.g. monitor INR"
                          className="w-40 px-2 py-1.5 rounded-md border border-stone-200 focus:border-brand-500 focus:outline-none text-sm" />
                      </td>
                      <td className="p-1">
                        {c.drugs.length > 1 && <button type="button" onClick={() => rmDrug(ci,di)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div className="flex justify-between">
          <button
            type="button"
            onClick={addCycle}
            data-testid="add-cycle-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-dashed border-brand-500 text-brand-700 hover:bg-brand-50 hover:border-brand-700 font-semibold transition-colors shadow-soft bg-white"
          >
            <Plus className="w-5 h-5" />
            Add cycle
          </button>
          <button data-testid="save-regimen-button" type="submit" className="px-5 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold">{isEdit ? "Save changes" : "Save regimen"}</button>
        </div>

        {!isEdit && (
          <div className="mt-6 flex items-start gap-3 px-4 py-3 rounded-xl bg-brand-50 border border-brand-100" data-testid="regimen-bottom-tip">
            <Lightbulb className="w-4 h-4 text-brand-600 mt-0.5 shrink-0" />
            <p className="text-sm text-brand-700">
              <b>Tip:</b> Start fresh with a blank regimen, or pick one from the dropdown above to copy its cycles. Either way, make sure your final schedule has unique changes — identical regimens are blocked by the system to keep the library clean.
            </p>
          </div>
        )}
      </form>

      {dupModal && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="duplicate-regimen-modal">
          <div className="bg-white rounded-2xl shadow-elevated w-full max-w-md border border-stone-200">
            <div className="p-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-display font-semibold text-stone-900 text-lg">Duplicate regimen</h3>
                <p className="text-sm text-stone-600 mt-1.5">
                  <b>Regimens exist — no duplication regimens required.</b> Edit a drug, days pattern, dose, route or any other cycle field before saving to differentiate this regimen from existing ones.
                </p>
              </div>
            </div>
            <div className="px-5 pb-5 flex justify-end">
              <button onClick={() => setDupModal(false)} data-testid="duplicate-regimen-ok" className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold">Got it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
